/**
 * Navigation.
 *
 * Clean paths - `/customers/12` - rather than the OpenSilver app's `#/Views/CustomerDetailPage
 * .xaml?customerId=12`. That shape existed because its `Frame` integrated with the browser journal
 * and the journal spoke in page URIs; there is no frame here, so there is no reason to keep it.
 *
 * LEGACY HASHES STILL WORK. Every link anybody has bookmarked, pasted into a ticket or mailed to a
 * colleague is in the old shape, and they outlive the UI that minted them. `LEGACY` is one row per
 * `GoTo*` method in the OpenSilver `NavigationService`, and it is applied at boot before anything
 * else looks at the URL.
 *
 * THE DEEP LINK IS CAPTURED BEFORE AUTHENTICATION. A refresh or a pasted URL carries the page, and
 * mounting it before sign-in would either flash a page the user cannot read or navigate away from the
 * login screen. `capture()` stashes it and replaces the URL; `start()` restores it once auth settles.
 * The same contract as `CaptureDeepLink` / `GoToStart`, and for the same reasons.
 */
import type { Container, Desktop } from 'webwin';
import { Disposables } from './ui/Disposables.js';
import type { CrmPage, PageContext, PageFactory } from './pages/page.js';

/**
 * Where the app was served before it took over `/`.
 *
 * Every page lived under `/app` while the OpenSilver client still owned the root, and links made
 * then are in tickets, mail and browser history. A path under it is read as the same route without
 * the prefix, and `capture()` rewrites the address bar to the clean form. The bundle's own files are
 * still served from `/app/` - only the pages moved.
 */
const LEGACY_BASE = '/app';

interface Route {
  readonly pattern: RegExp;
  readonly keys: readonly string[];
  readonly factory: PageFactory;
}

const routes: Route[] = [];
let notFound: PageFactory | null = null;

/**
 * Where a path should go instead, for the session as it stands - or null to render it. The shell
 * sets it: a host operator outside every organization has only the host's pages, and an organization
 * session has none of them.
 */
let guard: ((path: string) => string | null) | null = null;
export function setGuard(fn: (path: string) => string | null): void {
  guard = fn;
}

/**
 * Registers a route. `:name` captures one segment.
 *
 * First match wins, so order is the precedence - a literal route has to be registered before a
 * pattern that would also match it.
 */
export function route(pattern: string, factory: PageFactory): void {
  const keys: string[] = [];
  const source = pattern
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/\/:([A-Za-z0-9_]+)/g, (_match, key: string) => {
      keys.push(key);
      return '/([^/]+)';
    });
  routes.push({ pattern: new RegExp(`^${source}/?$`), keys, factory });
}

export function setNotFound(factory: PageFactory): void {
  notFound = factory;
}

/** A route as a URL this app can be navigated to. */
export function href(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}

/** The current route, with a pre-swap `/app` prefix stripped. '/' when at the root. */
export function currentRoute(): string {
  return stripLegacyBase(window.location.pathname);
}

/** Exported for its test. */
export function stripLegacyBase(path: string): string {
  if (path === LEGACY_BASE || path.startsWith(`${LEGACY_BASE}/`)) {
    const rest = path.slice(LEGACY_BASE.length);
    return rest.length === 0 ? '/' : rest;
  }
  return path.length === 0 ? '/' : path;
}

/**
 * The OpenSilver page URIs, mapped onto this app's routes.
 *
 * One row per `GoTo*` on the old `NavigationService`. The query parameter name is kept because that
 * is what is in the URLs already out there: `?customerId=12`, not `?id=12`.
 */
const LEGACY: ReadonlyArray<readonly [view: string, build: (q: URLSearchParams) => string]> = [
  ['CustomersPage', () => '/customers'],
  ['CustomerDetailPage', q => `/customers/${q.get('customerId') ?? ''}`],
  ['DevicesPage', () => '/devices'],
  ['DeviceDetailPage', q => `/devices/${q.get('deviceId') ?? ''}`],
  ['TicketsPage', () => '/tickets'],
  ['TicketDetailPage', q => `/tickets/${q.get('ticketId') ?? ''}`],
  ['InvoicesPage', () => '/invoices'],
  ['InvoiceDetailPage', q => `/invoices/${q.get('invoiceId') ?? ''}`],
  ['BillingPage', q => `/billing/${q.get('customerId') ?? ''}`],
  ['CatalogPage', () => '/catalog'],
  ['SoftwareLibraryPage', () => '/software'],
  ['ExpensesPage', () => '/expenses'],
  ['ReportsPage', () => '/reports'],
  ['ProfitLossPage', () => '/profit-loss'],
  ['NetworkPage', q => `/network/${q.get('customerId') ?? ''}`],
  ['MonitorDetailPage', q => `/monitors/${q.get('monitorId') ?? ''}`],
  ['SecurityPage', () => '/security'],
  ['AuditPage', () => '/audit'],
  ['SettingsPage', () => '/settings'],
  ['ChangePasswordPage', () => '/change-password'],
  ['LoginPage', () => '/login']
];

/**
 * Translates `#/Views/XxxPage.xaml?a=b` into a route, or returns null.
 *
 * Exported for its test: the table is 21 rows of string handling, which is exactly the kind of thing
 * that is wrong in one row and nobody notices until somebody's bookmark 404s.
 */
export function translateLegacyHash(hash: string): string | null {
  if (!hash.startsWith('#')) return null;

  // Decoded BEFORE the shape is checked, not after. A hash the browser minted reads `#/Views/...`,
  // but one that has been through a mail client, a ticket body or an encodeURIComponent arrives as
  // `#%2FViews%2F...` - and testing for `#/` first rejects exactly those, which are the links most
  // likely to be old enough to need translating. decodeURIComponent throws on a malformed escape, so
  // a mangled hash falls back to the raw text rather than taking the boot sequence down.
  let decoded: string;
  try {
    decoded = decodeURIComponent(hash.slice(1));
  } catch {
    decoded = hash.slice(1);
  }
  if (!decoded.startsWith('/')) return null;

  const [path = '', queryText = ''] = decoded.split('?', 2);
  const match = /\/Views\/([A-Za-z]+)\.xaml/.exec(path);
  if (!match) return null;
  const view = match[1];
  const entry = LEGACY.find(([name]) => name === view);
  if (!entry) return null;
  const built = entry[1](new URLSearchParams(queryText));
  // A detail route whose id did not come through would mount a page with an empty parameter, so it
  // degrades to the list it belongs to rather than to a broken detail view.
  return /\/$/.test(built) ? built.slice(0, -1) : built;
}

let pendingDeepLink: string | null = null;

/**
 * Stashes the route the app was opened at and replaces a legacy URL with its clean form.
 *
 * Must run before the first navigation. `replaceState`, not an assignment to `location`, so clearing
 * it adds no history entry - otherwise the browser Back button would return to the URL the user just
 * came from and re-trigger the whole dance.
 */
export function capture(): void {
  const legacy = translateLegacyHash(window.location.hash);
  const route = legacy ?? currentRoute();

  // Auth pages are flow states, not destinations worth restoring.
  const restorable = route !== '/' && !route.startsWith('/login') && !route.startsWith('/change-password');
  if (restorable) pendingDeepLink = route + (legacy ? '' : window.location.search);

  if (legacy || window.location.hash || currentRoute() !== window.location.pathname) {
    window.history.replaceState(null, '', href(legacy ?? currentRoute()) + window.location.search);
  }
}

/**
 * Where to land once authentication is settled: the captured deep link, or the dashboard.
 *
 * Returns the navigation's own render, so boot can await the first page rather than starting a
 * second one. Without that the landing page mounted twice on every load - the first mount's
 * in-flight fetch aborted by the second, which is harmless and looks exactly like a failed request
 * in the network list.
 */
export function start(): Promise<void> {
  const pending = pendingDeepLink;
  pendingDeepLink = null;
  return navigate(pending ?? '/dashboard', true);
}

// ---------------------------------------------------------------------
//  Mounting
// ---------------------------------------------------------------------

let host: Container | null = null;
let desktop: Desktop | null = null;
let current: { page: CrmPage; dispose: Disposables } | null = null;

/**
 * Notified after every navigation, however it arrived.
 *
 * The shell needs this to mark the rail entry whose page is showing, and `popstate` is not enough:
 * it fires for Back and Forward but NOT for the pushState a rail click performs, so a listener on
 * that alone highlights an entry only when the user navigates away from it and comes back. Raising
 * it here, at the end of the one function that mounts a page, is the only place that cannot miss a
 * route change.
 */
const navigated: Array<() => void> = [];
export function onNavigated(fn: () => void): void {
  navigated.push(fn);
}

/** Binds the router to the shell's content area. Called once, by the shell. */
export function mount(target: Container, owner: Desktop): void {
  host = target;
  desktop = owner;
  window.addEventListener('popstate', () => {
    void render();
  });
}

/**
 * Navigates to a route.
 *
 * A navigation to the address already showing REPLACES rather than pushes. Pushing it - a rail click
 * on the page you are on, a link to the list you are already looking at - leaves two identical
 * entries, and the next Back "does nothing" because it lands on the same page again.
 */
export function navigate(path: string, replace = false): Promise<void> {
  const url = href(path);
  const here = window.location.pathname + window.location.search;
  if (replace || url === here) window.history.replaceState(null, '', url);
  else window.history.pushState(null, '', url);
  return render();
}

/**
 * Tears the old page down and mounts the new one.
 *
 * The order is load-bearing: `unmount` first so a page can act while its controls still exist, then
 * `dispose` (which sets `cancelled`, so an in-flight load stops before it can render), and only then
 * `clear`. Clearing first would hand `unmount` an empty container.
 */
export async function render(): Promise<void> {
  if (!host || !desktop) return;

  let path = currentRoute();
  const redirect = guard?.(path) ?? null;
  if (redirect !== null && redirect !== path) {
    window.history.replaceState(null, '', href(redirect));
    path = redirect;
  }
  let factory: PageFactory | null = null;
  let params: Record<string, string> = {};

  for (const entry of routes) {
    const match = entry.pattern.exec(path);
    if (!match) continue;
    factory = entry.factory;
    params = {};
    entry.keys.forEach((key, index) => {
      params[key] = decodeURIComponent(match[index + 1] ?? '');
    });
    break;
  }

  if (!factory) factory = notFound;
  if (!factory) return;

  current?.page.unmount?.();
  current?.dispose.dispose();
  host.controls.clear();

  const dispose = new Disposables();
  const page = factory();
  current = { page, dispose };

  const ctx: PageContext = {
    params,
    query: new URLSearchParams(window.location.search),
    desktop,
    dispose,
    cancelled: () => dispose.disposed,
    // A page that has been navigated away from must not navigate. A callback finishing after Back
    // (a delete, a redirect, a late click handler) would otherwise push a new entry on top of the
    // page the user went back to, and Back would appear to take them somewhere else.
    go: (to, options) => {
      if (dispose.disposed) return;
      void navigate(to, options?.replace ?? false);
    }
  };

  try {
    await page.mount(host, ctx);
  } catch (error) {
    console.error(`CRM: ${path} failed to mount`, error);
  }

  // Another navigation (Back pressed while this page was still loading) has already mounted its own
  // page; laying out and announcing this one now would mark the rail for a page that is gone.
  if (current?.page !== page) return;

  // Lay the host out, and do not assume adding the page did it.
  //
  // `ControlCollection.add` calls `layoutDocked()`, which runs the DOCKING pass only - so a page
  // whose root asked for `hAlign: 'stretch'` keeps the 200px a Panel is born with until something
  // else resizes the host. The symptom is a page rendered in a narrow column down the left-hand side,
  // and it survives every measure flag being right, because the pass that reads them never ran.
  // `layout()` is anchors, alignment, docking and the subclass pass together, which is what a newly
  // filled container needs.
  host.layout();

  for (const fn of navigated) fn();
}

/** Unmounts whatever is showing - for sign-out, which leaves no page behind. */
export function clearCurrent(): void {
  current?.page.unmount?.();
  current?.dispose.dispose();
  current = null;
  host?.controls.clear();
}

export function back(): void {
  window.history.back();
}
